import { useState, type ReactNode } from 'react';
import * as React from 'react';
import { connect, type MapDispatchToPropsFunction } from 'react-redux';
import { matchPath } from 'react-router-dom';
import { useLocation } from 'react-use';
import { type Action } from 'redux';
import { type ThunkDispatch } from 'redux-thunk';
import { ServingSizeIcon } from '@hungryroot/components-pairing-card';

import {
  ProductCard,
  type ProductCardStyleVariant,
} from '@hungryroot/components-product-card';
import { type DietaryRestrictionsText } from '@hungryroot/dietary-restrictions';
import {
  DynamicConfigs,
  getDynamicConfigValue,
  type DynamicConfigInterface,
  type DynamicConfigTypes,
} from '@hungryroot/dynamic-configs';
import { type IconVariant } from '@hungryroot/icons';
import { type OrderIngredient } from '@hungryroot/orders';
import {
  PeriodicityTypes,
  useCustomerPeriodicities,
} from '@hungryroot/periodicities';
import { PDPModal } from '@hungryroot/rdp';
import {
  type PairingDetail,
  type PairingV1 as Pairing,
  type ProductV1 as Product,
} from '@hungryroot/rest';
import {
  EDIT_DELIVERY_GROCERIES_FOR_YOU_ROUTE,
  EDIT_DELIVERY_GROCERIES_ROUTE,
  EDIT_DELIVERY_RECIPES_FOR_YOU_ROUTE,
  EDIT_DELIVERY_RECIPES_ROUTE,
} from '@hungryroot/routes';
import { segmentAnalytics } from '@hungryroot/segment';
import { useCustomerServingSize } from '../../../api/v2/customers/[id]/pricing_config/get/get-customer-pricing-config';
import { toggleRdpModal } from '../../../frontend/react/src/customize/actions_creators';
import {
  maxSameItem,
  getVolumeLimit,
  getColdVolumeLimit,
} from '../../../frontend/react/src/customize/const';
import { type RequireAllOrNone } from '../../../utils/RequireAllOrNone';
import {
  type BootstrappingUseCartResult,
  type UseCartResult,
} from '../../cart/hooks/useCart/useCart';
import {
  getProductCount,
  getPairingCount,
  getLatestCartPairingBundle,
  isNotAddable,
  type IsNotAddableParams,
} from '../../cart/query';
import {
  type CustomizationState,
  useCustomization,
} from '../../CustomizationProvider/CustomizationProvider';
import { usePdpFooter } from '../../PDP/usePdpFooter';
import { RDPModal, type RDPModalProps } from '../../RDP/RDPModal/RDPModal';
import { productAdded, type ProductAddedParams } from '../../segment/segment';

declare global {
  interface Window {
    customerNevers: number[];
  }
}

interface Props {
  testid: string | undefined;
  toggleRdpModal?: (product: Pairing, ingredients?: OrderIngredient[]) => void;
  togglePdpModal?: (product: Product) => void;
  shopAction: {
    productData: Pairing | Product;
    isRecipe: boolean;
  };
  featuredCollectionSegmentData?: ProductAddedParams;
  productData: Pairing & Product & PairingDetail;
  isRecipe: boolean;
  title: string;
  credits?: number;
  badges?: Array<string>;
  binaryRatingRecipeScore?: Pick<
    Pairing,
    'id' | 'recipe_score' | 'liked' | 'num_ratings'
  >;
  ingredients?: Pairing['ingredients'];
  favoriteAction?: ReactNode;
  styleVariant: ProductCardStyleVariant;
  featuredImage: {
    src: string | undefined;
    alt: string;
  };
  label?: {
    icon?: IconVariant;
    text: string;
  };
  rdp?: React.ReactNode;
  showRdp?: () => void;
  overlayContent?: ReactNode;
  dietaryProductIngredients?: DietaryRestrictionsText[];
  dynamicConfigs?: DynamicConfigInterface<
    DynamicConfigTypes | DynamicConfigs
  >[];
  hideAddRemoveButton?: boolean;
  position?: number;
}

export function getInCart(
  productOrPairingId: number,
  isRecipe: boolean,
  useCartResult: UseCartResult | BootstrappingUseCartResult
) {
  const { cart } = useCartResult;
  if (cart === undefined) {
    return false;
  }

  if (isRecipe === true) {
    return getPairingCount(cart, productOrPairingId) > 0;
  }

  return getProductCount(cart, productOrPairingId) > 0;
}

export function getCount(
  productOrPairingId: number,
  isRecipe: boolean,
  useCartResult: Pick<UseCartResult | BootstrappingUseCartResult, 'cart'>
): number {
  const { cart } = useCartResult;
  if (cart === undefined) {
    return 0;
  }

  if (isRecipe === true) {
    return getPairingCount(cart, productOrPairingId);
  }

  return getProductCount(cart, productOrPairingId);
}

function BindToApplicationProductCard(
  props: RequireAllOrNone<Props, 'rdp' | 'showRdp'>
) {
  const {
    isRecipe,
    productData,
    testid,
    credits,
    binaryRatingRecipeScore,
    favoriteAction,
    toggleRdpModal,
    shopAction,
    featuredCollectionSegmentData,
    label,
    rdp,
    showRdp,
    overlayContent,
    dietaryProductIngredients,
    dynamicConfigs,
    hideAddRemoveButton,
    position,
    ...rest
  } = props;

  const [modalPairing, setModalPairing] = useState<{
    orderId?: number;
    pairing: Pairing;
    bundle?: RDPModalProps['initialBundle'];
  } | null>(null);
  const customization = useCustomization();
  const periodicitiesQuery = useCustomerPeriodicities();
  const triggerInteractionEvent = useTriggerInteractionEvent();
  const { data: periodicitiesData } = periodicitiesQuery;
  const customerNevers = periodicitiesData?.results.filter(
    (product) => product.periodicity === PeriodicityTypes.NEVER
  );
  const isInCustomerNevers = !!customerNevers?.find(
    (product) => product.product === productData.id
  );

  // The following is to get current cart count to pass down to AddRemoveButton
  const inCart =
    'cart' in customization
      ? getInCart(productData.id, isRecipe, customization)
      : false;

  let count = 0;

  if (inCart && 'cart' in customization) {
    count = getCount(productData.id, isRecipe, customization);
  }

  const rdpPairing = (productData: Pairing, inCart: boolean) => {
    if (toggleRdpModal === undefined) {
      return;
    }

    if (showRdp) {
      showRdp();
      return;
    }

    if ('cart' in customization) {
      const { cart } = customization;
      const { order_id: orderId } = cart;
      if (inCart) {
        const bundle = getLatestCartPairingBundle(cart, productData.id);
        setModalPairing({
          orderId,
          pairing: productData,
          bundle,
        });
        return;
      }

      setModalPairing({
        orderId,
        pairing: productData,
      });

      return;
    }

    toggleRdpModal(productData);
  };

  const removeProduct = () => {
    if (cartMutations === null) {
      return;
    }

    triggerInteractionEvent?.({
      type: isRecipe ? 'pairing' : 'product',
      id: productData.id,
      name: productData.name,
      interactionType: 'remove_from_cart',
      position,
      customization,
    });

    if (isRecipe === true) {
      cartMutations.removePairing(productData);
      return;
    }
    cartMutations.removeProduct(productData);
    return;
  };

  const addProduct = () => {
    triggerInteractionEvent?.({
      type: isRecipe ? 'pairing' : 'product',
      id: productData.id,
      name: productData.name,
      interactionType: 'add_to_cart',
      position,
      customization,
    });

    if (!isRecipe) {
      'mutations' in customization &&
        customization.mutations.addProduct({
          product: productData,
          dietaryProductIngredients,
        });
      return;
    }
    'mutations' in customization &&
      customization.mutations.addPairing({
        pairing: productData,
        dietaryProductIngredients,
      });
    return;
  };

  const cartMutations =
    'mutations' in customization ? customization.mutations : null;
  const customerPlanServingSize = useCustomerServingSize();

  const { ingredients } = productData;

  const [pdpModal, setPdpModal] = useState(false);

  const togglePdpFunction = () => {
    setPdpModal(true);
  };

  const { createFooterButton } = usePdpFooter({
    onAdd: () => setPdpModal(false),
    onRemove: () => setPdpModal(false),
  });

  return (
    <>
      {pdpModal && (
        <PDPModal
          stacked={false}
          onDismiss={() => setPdpModal(false)}
          id={productData.id}
          createFooterButton={createFooterButton}
          showOSNSection={true}
        />
      )}
      <ProductCard
        {...rest}
        isRecipe={isRecipe}
        onClick={
          isRecipe
            ? () => (toggleRdpModal ? rdpPairing(productData, inCart) : {})
            : togglePdpFunction
        }
        hideAddRemoveButton={hideAddRemoveButton}
        markedAsNever={isInCustomerNevers && !isRecipe && !overlayContent}
        testid={testid}
        credits={credits}
        binaryRatingRecipeScore={binaryRatingRecipeScore}
        favoriteAction={favoriteAction}
        ingredients={ingredients}
        badges={productData.badges}
        topLeftLabel={productData.is_new ? 'New' : ''}
        shopAction={
          cartMutations === null
            ? undefined
            : {
                ...shopAction,
                addProduct,
                removeProduct,
                productData,
                cartCount: count,
                isNotAddable: () => {
                  if (!('cart' in customization)) {
                    return false;
                  }
                  return isNotAddable({
                    cart: customization.cart,
                    maxSameItem,
                    volumeLimit: dynamicConfigs
                      ? getDynamicConfigValue(
                          dynamicConfigs,
                          DynamicConfigs.CustomizeVolumeLimit
                        )
                      : getVolumeLimit(),
                    coldVolumeLimit: dynamicConfigs
                      ? getDynamicConfigValue(
                          dynamicConfigs,
                          DynamicConfigs.CustomizeColdVolumeLimit
                        )
                      : getColdVolumeLimit(),
                    pairing:
                      isRecipe === true ? (productData as Pairing) : undefined,
                    product:
                      isRecipe === true ? undefined : (productData as Product),
                  } as IsNotAddableParams);
                },
                onAddToCart: () => {
                  if (featuredCollectionSegmentData === undefined) {
                    return;
                  }
                  productAdded(featuredCollectionSegmentData);
                },
              }
        }
        onViewItem={() => {
          triggerInteractionEvent?.({
            type: isRecipe ? 'pairing' : 'product',
            id: productData.id,
            name: productData.name,
            interactionType: 'view',
            position,
            customization,
          });
        }}
        label={label}
        overlayContent={overlayContent}
        servingSize={
          isRecipe && (
            <ServingSizeIcon
              customerPlanServingSize={customerPlanServingSize}
              pairingServingSize={productData.servings}
            />
          )
        }
      />
      {modalPairing !== null && (
        <RDPModal
          onDismiss={() => setModalPairing(null)}
          orderId={modalPairing.orderId}
          initialBundle={modalPairing.bundle}
          initialPairing={modalPairing.pairing}
        />
      )}
      {rdp}
    </>
  );
}

const mapDispatchToProps: MapDispatchToPropsFunction<unknown, unknown> = (
  dispatch: ThunkDispatch<unknown, unknown, Action>
) => {
  return {
    toggleRdpModal: (pairing: Pairing) => dispatch(toggleRdpModal(pairing)),
  };
};

export default connect(null, mapDispatchToProps)(BindToApplicationProductCard);

function useTriggerInteractionEvent() {
  const isShopPage = useIsShopPage();

  if (isShopPage) {
    return sendInteractionEvent;
  }
}

type TriggerInteractionEventArgs = {
  type: 'product' | 'pairing';
  id: number;
  name: string;
  interactionType: 'add_to_cart' | 'remove_from_cart' | 'view';
  position?: number;
  customization: CustomizationState;
};

function sendInteractionEvent({
  type,
  id,
  name,
  interactionType,
  position,
  customization,
}: TriggerInteractionEventArgs) {
  if (
    position === undefined ||
    !Number.isInteger(position) ||
    !('cart' in customization)
  ) {
    return;
  }

  if (type === 'product') {
    segmentAnalytics.track('Product Interaction', {
      event_name: 'Product Interaction',
      order_id: customization.cart.order_id,
      product_id: id,
      product_name: name,
      interaction_type: interactionType,
      page_name: 'Shop',
      position,
    });
  } else {
    segmentAnalytics.track('Pairing Interaction', {
      event_name: 'Pairing Interaction',
      order_id: customization.cart.order_id,
      pairing_id: id,
      pairing_name: name,
      interaction_type: interactionType,
      page_name: 'Shop',
      position,
    });
  }
}

const shopRoutes = [
  EDIT_DELIVERY_RECIPES_ROUTE,
  EDIT_DELIVERY_GROCERIES_ROUTE,
  EDIT_DELIVERY_GROCERIES_FOR_YOU_ROUTE,
  EDIT_DELIVERY_RECIPES_FOR_YOU_ROUTE,
];

function useIsShopPage() {
  const { pathname } = useLocation();

  if (!pathname) {
    return false;
  }

  for (const route of shopRoutes) {
    if (matchPath(route, pathname)) {
      return true;
    }
  }

  return false;
}
